/**
 * Monitor Clip Production Hook
 *
 * A React hook that encapsulates all clip production workflow tracking logic.
 * This hook provides a clean interface for components to track clip production
 * performance without managing tracking state or workflow logic directly.
 */

import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useCallback, useRef } from 'react';

import { ActionEventType, ActionType } from '../types';

/**
 * Base tracking hook interface
 */
interface BaseTrackingHook {
  /** Whether tracking is currently active */
  isTracking: boolean;
  /** Whether tracking is enabled in configuration */
  isEnabled: boolean;
}

/**
 * Clip production specific metadata
 */
export interface ClipProductionMetadata {
  theme: string;
  customTheme?: string;
  platform: string;
  duration: number | null;
  useSpeakerFilter: boolean;
  selectedSpeakersCount: number;
  useGoalFilter: boolean;
  selectedGoal?: string;
  useAudienceFilter: boolean;
  selectedAudiencesCount: number;
  storyId?: string;
  videoId?: string;
}

/**
 * Clip production tracking configuration
 */
export interface ClipProductionConfig {
  /** Whether to track clip production workflows */
  enabled?: boolean;
  /** Whether to include detailed metadata */
  includeDetailedMetadata?: boolean;
  /** Whether to track performance metrics */
  trackPerformance?: boolean;
}

/**
 * Clip production tracking hook interface
 */
export interface ClipProductionTrackingHook extends BaseTrackingHook {
  /** Start clip production action tracking */
  startProduction: (metadata: ClipProductionMetadata) => string | null;
  /** Complete clip production action (success) */
  completeProduction: (
    clipsProduced: number,
    retryCount: number,
    additionalMetadata?: Record<string, unknown>,
  ) => void;
  /** Fail clip production action */
  failProduction: (
    retryCount: number,
    maxRetries: number,
    reason: string,
    additionalMetadata?: Record<string, unknown>,
  ) => void;
  /** Cancel clip production action */
  cancelProduction: (reason?: string) => void;
  /** Current transaction ID */
  transactionId: string | null;
  /** Whether production is currently being tracked */
  isProducing: boolean;
}

/**
 * Hook for monitoring clip production actions
 *
 * @param config - Optional configuration for tracking behavior
 * @returns Clip production tracking interface
 */
export function useMonitorClipProduction(
  config: ClipProductionConfig = {},
): ClipProductionTrackingHook {
  const rootStore = useStore();
  const videoCreator = useVideoCreatorStore();

  // Configuration with defaults
  const {
    enabled = true,
    includeDetailedMetadata = true,
    trackPerformance = true,
  } = config;

  // Tracking state
  const transactionIdRef = useRef<string | null>(null);
  const hasCompletedActionRef = useRef(false);
  const isTrackingRef = useRef(false);
  const actionMetadataRef = useRef<Record<string, any> | null>(null);

  // Check if tracking is enabled
  const isEnabled =
    enabled && rootStore.analyticsStore.getMonitoringConfig().enabled;

  /**
   * Start clip production action tracking
   */
  const startProduction = useCallback(
    (metadata: ClipProductionMetadata): string | null => {
      if (!isEnabled) {
        return null;
      }

      try {
        // Reset tracking state
        hasCompletedActionRef.current = false;
        isTrackingRef.current = true;

        // Prepare action metadata
        const actionMetadata = {
          ...metadata,
          // Add context from stores if includeDetailedMetadata is enabled
          ...(includeDetailedMetadata && {
            storyTitle: videoCreator.story?.title,
            videoTitle: videoCreator.currentVideo?.title,
            organizationId: videoCreator.organization?.id,
            organizationSlug: videoCreator.organization?.slug,
            environment: rootStore.userIdentityStore?.environment,
          }),
          // Add performance tracking flag
          trackPerformance,
          // Add start timestamp for duration calculation
          startedAt: new Date().toISOString(),
        };

        // Store metadata for duration calculation
        actionMetadataRef.current = actionMetadata;

        // Prepare context
        const actionContext = {
          business: {
            storyId: metadata.storyId,
            videoId: metadata.videoId,
            organization: videoCreator.organization
              ? {
                  id: videoCreator.organization.id,
                  name:
                    videoCreator.organization.title || 'Unknown Organization',
                  slug: videoCreator.organization.slug,
                }
              : undefined,
            environment: rootStore.userIdentityStore?.environment || 'unknown',
          },
        };

        // Start action
        transactionIdRef.current = rootStore.analyticsStore.startUserAction(
          ActionEventType.CLIP_PRODUCTION,
          ActionType.ACTION,
          undefined,
          actionContext,
          actionMetadata,
        );

        isTrackingRef.current = true;

        return transactionIdRef.current;
      } catch (error) {
        console.warn('Failed to start clip production tracking:', error);
        return null;
      }
    },
    [
      isEnabled,
      includeDetailedMetadata,
      trackPerformance,
      rootStore,
      videoCreator,
    ],
  );

  /**
   * Complete clip production action (success)
   */
  const completeProduction = useCallback(
    (
      clipsProduced: number,
      retryCount: number,
      additionalMetadata: Record<string, unknown> = {},
    ): void => {
      if (
        !isEnabled ||
        !transactionIdRef.current ||
        hasCompletedActionRef.current
      ) {
        return;
      }

      try {
        const completedAt = new Date();
        const completionMetadata = {
          clipsProduced,
          retryCount,
          ...additionalMetadata,
          // Add timestamp for performance analysis
          completedAt: completedAt.toISOString(),
          // Calculate duration if we have start time
          ...(actionMetadataRef.current?.startedAt && {
            durationMs:
              completedAt.getTime() -
              new Date(actionMetadataRef.current.startedAt).getTime(),
          }),
        };

        // Calculate performance metrics if enabled
        const performanceMetrics = trackPerformance
          ? {
              duration: completionMetadata.durationMs,
              // Add other performance metrics as needed
            }
          : undefined;

        rootStore.analyticsStore.completeUserAction(
          transactionIdRef.current,
          completionMetadata,
          performanceMetrics,
        );

        hasCompletedActionRef.current = true;
        isTrackingRef.current = false;
      } catch (error) {
        console.warn('Failed to complete clip production tracking:', error);
      }
    },
    [isEnabled, trackPerformance, rootStore],
  );

  /**
   * Fail clip production action
   */
  const failProduction = useCallback(
    (
      retryCount: number,
      maxRetries: number,
      reason: string,
      additionalMetadata: Record<string, unknown> = {},
    ): void => {
      if (
        !isEnabled ||
        !transactionIdRef.current ||
        hasCompletedActionRef.current
      ) {
        return;
      }

      try {
        const failureMetadata = {
          retryCount,
          maxRetries,
          reason,
          ...additionalMetadata,
          // Add timestamp for failure analysis
          failedAt: new Date().toISOString(),
        };

        rootStore.analyticsStore.failUserAction(
          transactionIdRef.current,
          reason,
          failureMetadata,
        );

        hasCompletedActionRef.current = true;
        isTrackingRef.current = false;
      } catch (error) {
        console.warn('Failed to fail clip production tracking:', error);
      }
    },
    [isEnabled, rootStore],
  );

  /**
   * Cancel clip production action
   */
  const cancelProduction = useCallback(
    (reason: string = 'User cancelled clip production'): void => {
      if (
        !isEnabled ||
        !transactionIdRef.current ||
        hasCompletedActionRef.current
      ) {
        return;
      }

      try {
        rootStore.analyticsStore.cancelUserAction(
          transactionIdRef.current,
          reason,
        );
        hasCompletedActionRef.current = true;
        isTrackingRef.current = false;
      } catch (error) {
        console.warn('Failed to cancel clip production tracking:', error);
      }
    },
    [isEnabled, rootStore],
  );

  return {
    // State
    isTracking: isTrackingRef.current,
    isEnabled,
    transactionId: transactionIdRef.current,
    isProducing: isTrackingRef.current,

    // Actions
    startProduction,
    completeProduction,
    failProduction,
    cancelProduction,
  };
}
